import { Injectable, inject } from '@angular/core'
import { Observable, from } from 'rxjs'
import { TicketAssigneeProvider, AssigneeOption, TicketUser } from '@solidgroup-agency/ticket-system/models'
import { SchoolService } from '@awo-care/schools'
import { resolveAssigneeDisplayName } from './awo-assignee-name.utils'
import { FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';
import { Firestore, collection, getDocs, query, where } from 'firebase/firestore';

/**
 * Mitarbeiter-Accounts, die nie als Assignee oder @mention-Kandidat erscheinen
 * sollen (Test-/Rollen-Accounts). Vergleich case-insensitive über die E-Mail.
 */
const EXCLUDED_ASSIGNEE_EMAILS = new Set(['pm.landvogt.consulting@awo-hs.org'])

/** Rollen, denen Tickets zugewiesen werden können (Mitarbeiter und Teamleitung seit #1277). */
const ASSIGNABLE_ROLES = new Set<string>(['admin', 'manager', 'teamlead', 'employee'])

/** TicketUser augmented with the role field — consumed by AwoAssigneePicker. */
export interface AwoTicketUser extends TicketUser {
  role: 'admin' | 'manager' | 'teamlead' | 'employee'
}

export function isAwoTicketUser(u: TicketUser): u is AwoTicketUser {
  return ASSIGNABLE_ROLES.has((u as AwoTicketUser).role)
}

@Injectable({ providedIn: 'root' })
export class AwoAssigneeProvider implements TicketAssigneeProvider {
  private firestore = inject(FIREBASE_FIRESTORE)
  private schoolService = inject(SchoolService)

  getAssignees(facilityId?: string): Observable<AssigneeOption[]> {
    return from(this.loadAssignees(facilityId))
  }

  private async loadAssignees(facilityId?: string): Promise<AssigneeOption[]> {
    const usersRef = collection(this.firestore, 'employees')
    const q = query(usersRef, where('isActive', '==', true))
    // Mitarbeiter lesen nur ihren eigenen employees-Datensatz (Rules): ohne Berechtigung
    // bleibt die Zuweisungsliste leer, statt dass der Ticketdialog scheitert (#1271).
    const snapshot = await getDocs(q).catch(() => null)
    if (!snapshot) return []

    const users = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }) as Record<string, unknown>)
      .filter((u) => ASSIGNABLE_ROLES.has(u['role'] as string))
      .filter((u) => !EXCLUDED_ASSIGNEE_EMAILS.has(((u['email'] as string) ?? '').toLowerCase()))

    let facilityKreis: string | null = null
    if (facilityId) {
      const school = this.schoolService.schools().find((s) => s.id === facilityId)
      facilityKreis = school?.kreis ?? null
    }

    return users.map((u) => {
      const displayName = resolveAssigneeDisplayName({
        firstName: u['firstName'] as string | undefined,
        lastName: u['lastName'] as string | undefined,
        email: u['email'] as string | undefined,
      })
      const role = u['role'] as AwoTicketUser['role']
      const isMatch = !facilityId
        ? false
        : u['allAreas'] === true ||
          ((u['assignedSchools'] as string[] | undefined)?.includes(facilityId) ?? false) ||
          (facilityKreis != null &&
            ((u['assignedKreise'] as string[] | undefined)?.includes(facilityKreis) ?? false))

      const firstName = u['firstName'] as string | undefined
      const lastName = u['lastName'] as string | undefined
      const userWithRole: AwoTicketUser = {
        uid: u['id'] as string,
        email: u['email'] as string,
        displayName,
        role,
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
      }
      return {
        user: userWithRole,
        group: isMatch ? 'Passend für diese Einrichtung' : 'Weitere Mitarbeiter',
        isRecommended: isMatch,
      }
    })
  }
}
